import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { MergeBlocks, Review, ShapeViolation } from "../../../api/content/reviewDecoders";
import { rebuildOnMyPc, updateFromMain } from "../../../api/threads/maintainedRebuildsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { appPaths } from "../../../shared/routing/appPaths";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import { useReviewTermNames } from "../conflicts/conflictTermNames";
import { checkNames } from "../state/mergeBlockReport";
import CheckFindingView from "../checks/CheckFindingView";
import { withItemNames } from "../checks/checkFindings";

/** R-3: explain the server's block before a decision; keep baseline violations apart. */
export default function MergeBlocksNotice({ review, technical, onChanged }: { review: Review; technical: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const blocks = review.mergeBlocks;
  const shapes = blocks.shapeViolations;
  const labels = withItemNames(useReviewTermNames(review), review.items, [...shapes.introduced, ...shapes.preExisting]);
  const checks = checkNames(review, blocks.inconsistencyCheckIds, t);
  const uncompared = checkNames(review, shapes.uncomparedCheckIds, t);
  const revise = checks.length > 0 || shapes.introduced.length > 0 || shapes.unlistedIntroduced > 0;
  if (!blocks.blocked && shapes.preExisting.length === 0 && uncompared.length === 0) return null;
  const violations = (entries: readonly ShapeViolation[]) => (
    <ul className="space-y-2">
      {entries.map((entry, index) => (
        <li key={`${entry.checkId}:${String(index)}`}>
          <span className="font-medium">{entry.checkTitle ?? entry.checkId}</span>{" — "}
          <CheckFindingView finding={entry.finding} labels={labels} technical={technical} />
        </li>
      ))}
    </ul>
  );
  return (
    <StatusMessage tone={blocks.blocked ? "error" : "info"} title={blocks.blocked ? t("reviews.mergeBlocks.title") : undefined} className="space-y-2 text-sm">
      {blocks.blocked ? (
        <div className="space-y-2">
          {revise ? <p>{t("reviews.mergeBlocks.explanation")}</p> : null}
          <MaintainedBlocks review={review} onChanged={onChanged} />
          {checks.length > 0 ? (
            <div>
              <h3 className="font-semibold">{t("reviews.mergeBlocks.inconsistent")}</h3>
              <ul className="list-disc pl-5">{checks.map((name, index) => <li key={index}>{name}</li>)}</ul>
            </div>
          ) : null}
          {shapes.introduced.length > 0 || shapes.unlistedIntroduced > 0 ? (
            <div>
              <h3 className="font-semibold">{t("reviews.mergeBlocks.introduced")}</h3>
              {violations(shapes.introduced)}
              {shapes.unlistedIntroduced > 0 ? <p>{t("reviews.mergeBlocks.more", { count: shapes.unlistedIntroduced })}</p> : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {uncompared.length > 0 ? <p>{t("reviews.mergeBlocks.uncompared", { checks: uncompared.join(", ") })}</p> : null}
      {shapes.preExisting.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer font-medium">{t("reviews.mergeBlocks.preExisting")}</summary>
          {violations(shapes.preExisting)}
        </details>
      ) : null}
    </StatusMessage>
  );
}

/**
 * ADR-0044: items the change keeps up to date that would land out of date, each with its fix,
 * and the actions those fixes name: the rebuild on the caller's PC, or the update from main.
 */
function MaintainedBlocks({ review, onChanged }: { review: Review; onChanged: () => void }) {
  const { t } = useTranslation();
  const action = useSubmission();
  const blocks: MergeBlocks = review.mergeBlocks;
  const rebuild = blocks.maintainedRebuild;
  if (blocks.maintainedItems.length === 0 && blocks.maintainedCycle.length === 0) return null;
  const fixes = new Set(blocks.maintainedItems.map((block) => block.fix));
  const changeSetId = review.changeSet.changeSetId;
  const busy = action.state.status === "submitting";
  return (
    <div className="space-y-2">
      <h3 className="font-semibold">{t("reviews.mergeBlocks.maintained")}</h3>
      <ul className="list-disc pl-5">
        {blocks.maintainedItems.map((block) => (
          <li key={block.itemId}>
            {t(`reviews.mergeBlocks.maintainedFix.${block.fix}`, { name: block.name ?? block.itemId, problem: block.problem ?? "" })}
          </li>
        ))}
      </ul>
      {blocks.maintainedCycle.length > 0 ? <p>{t("reviews.mergeBlocks.maintainedCycle", { count: blocks.maintainedCycle.length })}</p> : null}
      {rebuild?.state === "running" || rebuild?.state === "waiting_for_host" || rebuild?.state === "failed" ? (
        <p>{t(`reviews.mergeBlocks.rebuildState.${rebuild.state}`, { problem: rebuild.problem ?? "" })}</p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {fixes.has("rebuild") ? (
          <AppButton size="xs" disabled={busy} onClick={() => {
            action.submit(async () => {
              await rebuildOnMyPc(changeSetId);
              onChanged();
            });
          }}>
            {t("reviews.mergeBlocks.rebuildHere")}
          </AppButton>
        ) : null}
        {fixes.has("update_from_main") ? (
          <AppButton size="xs" disabled={busy} onClick={() => {
            action.submit(async () => {
              await updateFromMain(changeSetId, review.head);
              onChanged();
            });
          }}>
            {t("reviews.mergeBlocks.updateFromMain")}
          </AppButton>
        ) : null}
        {rebuild?.threadId == null ? null : <Link to={appPaths.thread(rebuild.threadId)} className="app-link underline">{t("reviews.mergeBlocks.openRebuild")}</Link>}
      </div>
      {action.state.status === "failed" ? <ErrorNotice error={action.state.error} compact={true} /> : null}
    </div>
  );
}
